import React, { useCallback, useEffect, useState } from 'react';
import PropTypes from 'prop-types';
import IronSightMark from '../../shared/IronSightMark';
import apiService from '../../../services/apiService';
import { useWorkspace } from '../../../contexts/WorkspaceContext';
import { formatCurrency } from '../../../utils/formatting';
import UserSearch from '../../shared/UserSearch';
import SOURCE_LABELS from '../../../config/sourceLabels';
import { unitAgingLabel } from '../../../utils/dealershipSettings';
import { useDealershipSettings } from '../../../contexts/settingsContext';
import {
    THINKING_STEPS, STEP_DURATIONS, stepState, fmtNum, fmtPct, percentileBucket, trendBucket, trendArrow,
    marketplaceBadges, marketplaceInsights, resolveRiskBadge, riskBadgeTitle, cachedLabel,
    peerVerdictArrow, mismatchText, agentTaskUsers, taskPrefill, buildTaskPayload,
} from '../../../utils/askIronSight';
import './FastTrackAgentTab.css';

// ── Thinking loader (steps and timing: utils/askIronSight) ─────────────────

function AgentThinking({ unit }) {
    const [step, setStep] = useState(0);

    useEffect(() => {
        if (step >= THINKING_STEPS.length - 1) return;
        const t = setTimeout(() => setStep(s => s + 1), STEP_DURATIONS[step]);
        return () => clearTimeout(t);
    }, [step]);

    const unitLabel = [unit?.YEAR_MANUF, unit?.MAKE, unit?.MODEL].filter(Boolean).join(' ');

    return (
        <div className="tab-content fta-root">
            <div className="fta-thinking-card">
                <div className="fta-thinking-header">
                    <div className="fta-thinking-pulse" />
                    <div>
                        <div className="fta-thinking-title">IronSight is thinking</div>
                        {unitLabel && <div className="fta-thinking-unit">{unitLabel}</div>}
                    </div>
                </div>
                <div className="fta-thinking-steps">
                    {THINKING_STEPS.map((s, i) => {
                        const state = stepState(i, step);
                        return (
                            <div key={i} className={`fta-thinking-step fta-thinking-step--${state}`}>
                                <div className="fta-thinking-step-icon">
                                    {state === 'done'
                                        ? <span className="fta-thinking-check">✓</span>
                                        : state === 'active'
                                            ? <span className="fta-thinking-spinner" />
                                            : <span className="fta-thinking-dot" />}
                                </div>
                                <div className="fta-thinking-step-body">
                                    <span className="fta-thinking-step-label">{s.label}</span>
                                    {state !== 'pending' && (
                                        <span className="fta-thinking-step-detail">{s.detail}</span>
                                    )}
                                </div>
                            </div>
                        );
                    })}
                </div>
            </div>
        </div>
    );
}

// ── Marketplace Performance helpers ───────────────────────────────────────────

function PercentileRow({ label, block, unit, digits = 0, reverse = false }) {
    if (!block) return null;
    const { subject, cohort_median, percentile } = block;
    const bucket = percentileBucket(percentile, reverse);
    const markerLeft = percentile != null ? `${Math.max(0, Math.min(100, percentile))}%` : '0%';
    return (
        <div className="fta-marketplace-metric">
            <span className="fta-mp-metric-label">{label}</span>
            <span className="fta-mp-metric-subject">
                {fmtNum(subject, digits)}{unit || ''}
            </span>
            <span className="fta-mp-metric-median">
                med {fmtNum(cohort_median, digits)}{unit || ''}
            </span>
            {percentile == null ? (
                <span className="fta-mp-metric-na" title="Insufficient cohort data">—</span>
            ) : (
                <span
                    className={`fta-percentile-bar fta-percentile-bar--${bucket}`}
                    title={`${percentile.toFixed(0)}th percentile`}
                >
                    <span className="fta-percentile-fill" style={{ width: markerLeft }} />
                    <span className="fta-percentile-marker" style={{ left: markerLeft }} />
                </span>
            )}
        </div>
    );
}

function TrendItem({ label, value }) {
    const bucket = trendBucket(value);
    return (
        <div className={`fta-trend-item fta-trend-item--${bucket}`}>
            <span className="fta-trend-label">{label}</span>
            <span className="fta-trend-value">
                <span className="fta-trend-arrow">{trendArrow(value)}</span>
                {fmtPct(value)}
            </span>
        </div>
    );
}

function MarketplacePerformance({ mp }) {
    if (!mp) return null;

    const th = mp.th_percentiles || {};
    const ga = mp.ga4_percentiles || {};
    const thTrend = mp.th_trend_7d_vs_prior || {};
    const gaTrend = mp.ga4_trend_7d_vs_prior || {};
    const badges = marketplaceBadges(mp);
    const insights = marketplaceInsights(mp);

    return (
        <section className="fta-section fta-marketplace">
            <div className="fta-marketplace-header">
                <h3 className="fta-section-title">Marketplace Performance</h3>
                <span className="fta-marketplace-subtitle">
                    Benchmarked against {fmtNum(mp.cohort_size)} same-make/model/year peers
                </span>
            </div>

            {badges.length > 0 && (
                <div className="fta-marketplace-badges">
                    {badges.map(b => (
                        <span key={b.key} className={`fta-marketplace-badge fta-marketplace-badge--${b.key}`}>
                            {b.label}
                        </span>
                    ))}
                </div>
            )}

            <div className="fta-marketplace-columns">
                <div className="fta-marketplace-col">
                    <h4 className="fta-marketplace-col-title">{SOURCE_LABELS.marketplace} (30d)</h4>
                    <PercentileRow label="Impressions" block={th.impressions} />
                    <PercentileRow label="Details Views" block={th.details_views} />
                    <PercentileRow label="CTR" block={th.ctr} unit="%" digits={2} />
                    <PercentileRow label="Email Leads" block={th.email_leads} />
                    <PercentileRow label="Watchlists" block={th.watchlists} />
                </div>
                <div className="fta-marketplace-col">
                    <h4 className="fta-marketplace-col-title">{SOURCE_LABELS.website} (30d)</h4>
                    <PercentileRow label="Pageviews" block={ga.pageviews} />
                    <PercentileRow label="Unique Users" block={ga.unique_users} />
                    <PercentileRow label="Avg Session" block={ga.avg_session_sec} unit="s" />
                    <PercentileRow label="Bounce Rate" block={ga.bounce_rate} unit="%" digits={1} reverse />
                </div>
            </div>

            <div className="fta-trend-row">
                <TrendItem label="Impressions" value={thTrend.impressions_pct} />
                <TrendItem label="Leads" value={thTrend.leads_pct} />
                <TrendItem label="Watchlists" value={thTrend.watchlists_pct} />
                <TrendItem label="Pageviews" value={gaTrend.pageviews_pct} />
                <TrendItem label="Avg Session" value={gaTrend.session_pct} />
            </div>

            <div className="fta-insight-strip">
                {insights.map((i) => (
                    <div key={i.key} className={`fta-insight-item fta-insight-item--${i.key}`}>{i.text}</div>
                ))}
            </div>
        </section>
    );
}

// The risk badge logic lives in utils/askIronSight (shared with the v2 tab).
export { resolveRiskBadge };

// ── Main component ────────────────────────────────────────────────────────────

function FastTrackAgentTab({ agent, loading, error, unit, onRegenerate }) {
    // The recommendations are worth reading in a read-only workspace; turning one
    // into a task is a write the backend will refuse, so it is not offered.
    const { readOnly } = useWorkspace();
    const { aging } = useDealershipSettings();
    const ageLabel = unit ? unitAgingLabel(unit, aging) : '';
    const canEdit = !readOnly;
    const [copied, setCopied] = useState(false);
    const [taskFromRec, setTaskFromRec] = useState(null);
    const [taskCreating, setTaskCreating] = useState(false);
    const [taskCreated, setTaskCreated] = useState(new Set());
    const [taskUsers, setTaskUsers] = useState([]);
    const [taskAssignee, setTaskAssignee] = useState('');
    const [taskDueDate, setTaskDueDate] = useState('');
    const [taskTitle, setTaskTitle] = useState('');
    const [taskNotes, setTaskNotes] = useState('');
    const [taskType, setTaskType] = useState('other');
    const [taskSuggestedPrice, setTaskSuggestedPrice] = useState('');
    const [taskTargetContact, setTaskTargetContact] = useState('');
    const [taskDescription, setTaskDescription] = useState('');

    useEffect(() => {
        apiService.getActivityUsers().then(d => setTaskUsers(agentTaskUsers(d.users))).catch(() => {});
    }, []);

    const handleCreateTask = async () => {
        if (!taskFromRec || !unit) return;
        setTaskCreating(true);
        try {
            await apiService.createActivity(buildTaskPayload(unit, {
                type: taskType,
                title: taskTitle,
                notes: taskNotes,
                description: taskDescription,
                suggestedPrice: taskSuggestedPrice,
                assignee: taskAssignee,
                dueDate: taskDueDate,
            }));
            setTaskCreated(prev => new Set([...prev, taskFromRec.priority]));
            setTaskFromRec(null);
            setTaskAssignee('');
            setTaskDueDate('');
            setTaskTitle('');
            setTaskNotes('');
            setTaskDescription('');
        } catch (err) {
            console.error('Failed to create task from recommendation', err);
        } finally {
            setTaskCreating(false);
        }
    };

    const handleCopy = useCallback(() => {
        if (!agent?.suggested_description) return;
        navigator.clipboard.writeText(agent.suggested_description).then(() => {
            setCopied(true);
            setTimeout(() => setCopied(false), 2000);
        });
    }, [agent?.suggested_description]);

    if (loading) {
        return <AgentThinking unit={unit} />;
    }

    if (error && !loading) {
        return (
            <div className="tab-content fta-root">
                <div className="fta-error-card">
                    <p>{error}</p>
                    <button className="fta-regen-btn" onClick={onRegenerate}>
                        Try Again
                    </button>
                </div>
            </div>
        );
    }

    if (!loading && !agent && !error) {
        return (
            <div className="tab-content fta-root">
                <div className="fta-empty-card">
                    <span className="fta-empty-pulse" />
                    <p className="fta-empty-text">Analysing unit data…</p>
                </div>
            </div>
        );
    }

    const unitLabel = [unit?.YEAR_MANUF, unit?.MAKE, unit?.MODEL].filter(Boolean).join(' ');
    const stockNo = unit?.StockNumber || unit?.stock_number;
    const riskBadge = resolveRiskBadge(agent, unit);

    return (
        <div className="tab-content fta-root">

            {/* Header band — reticle eyebrow, unit identity, risk pill, regenerate */}
            <header className="fta-header">
                <div className="fta-header-main">
                    <span className="fta-eyebrow">
                        <span className="fta-reticle" aria-hidden="true"><IronSightMark size={13} /></span> IRONSIGHT
                    </span>
                    <h2 className="fta-header-title">
                        {unitLabel || 'Unit'}
                        {stockNo && <span className="fta-header-stock">#{stockNo}</span>}
                    </h2>
                    {(unit?.DaysInStock != null || ageLabel) && (
                        <p className="fta-header-subtitle">
                            {unit?.DaysInStock != null && (
                                <span className="fta-header-days">{unit.DaysInStock} days in stock</span>
                            )}
                            {ageLabel && (
                                <span className="fta-header-status">{ageLabel}</span>
                            )}
                        </p>
                    )}
                </div>
                <div className="fta-header-side">
                    {riskBadge && (
                        <span
                            className={`fta-risk-pill fta-risk-pill--${riskBadge.level.toLowerCase()}`}
                            title={riskBadgeTitle(riskBadge)}
                        >
                            <span className="fta-risk-dot" aria-hidden="true" />
                            {riskBadge.level} risk{riskBadge.pct != null ? ` · ${riskBadge.pct}%` : ''}
                        </span>
                    )}
                    {cachedLabel(agent) && (
                        <span className="fta-cached-note">{cachedLabel(agent)}</span>
                    )}
                    <button className="fta-refresh-btn" onClick={onRegenerate} title="Regenerate recommendations" aria-label="Regenerate recommendations">
                        <svg viewBox="0 0 20 20" fill="currentColor" width="14" height="14">
                            <path fillRule="evenodd" d="M4 2a1 1 0 011 1v2.101a7.002 7.002 0 0111.601 2.566 1 1 0 11-1.885.666A5.002 5.002 0 005.999 7H9a1 1 0 010 2H4a1 1 0 01-1-1V3a1 1 0 011-1zm.008 9.057a1 1 0 011.276.61A5.002 5.002 0 0014.001 13H11a1 1 0 110-2h5a1 1 0 011 1v5a1 1 0 11-2 0v-2.101a7.002 7.002 0 01-11.601-2.566 1 1 0 01.61-1.276z" clipRule="evenodd" />
                        </svg>
                    </button>
                </div>
            </header>

            {/* Verdict hero — sage gradient callout */}
            <section className="fta-hero">
                <div className={`fta-hero-icon fta-hero-icon--${agent.performance_verdict.toLowerCase()}`}>
                    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" width="22" height="22" aria-hidden="true">
                        <circle cx="12" cy="12" r="9" />
                        <circle cx="12" cy="12" r="3.5" />
                        <path d="M12 1.5v4M12 18.5v4M1.5 12h4M18.5 12h4" strokeLinecap="round" />
                    </svg>
                </div>
                <div className="fta-hero-body">
                    <div className="fta-hero-lead-row">
                        <span className="fta-hero-lead">Agent Verdict</span>
                        <span className={`fta-verdict-badge fta-verdict-badge--${agent.performance_verdict.toLowerCase()}`}>
                            {agent.performance_verdict}
                        </span>
                        {agent.urgency && (
                            <span className={`fta-urgency-chip fta-urgency-chip--${agent.urgency.toLowerCase()}`}>
                                Action urgency: {agent.urgency}
                            </span>
                        )}
                    </div>
                    <p className="fta-hero-headline">{agent.headline}</p>
                    {agent.urgency_reason && (
                        <p className="fta-hero-rationale">{agent.urgency_reason}</p>
                    )}
                </div>
            </section>

            {/* Why the agent says this — root causes as reticle-dot rows */}
            {agent.root_causes?.length > 0 && (
                <section className="fta-section fta-why">
                    <h3 className="fta-section-title">Why the agent says this</h3>
                    <div className="fta-why-list">
                        {agent.root_causes.map((rc, i) => (
                            <div key={i} className="fta-why-row">
                                <span className="fta-why-dot" aria-hidden="true" />
                                <div className="fta-why-body">
                                    <div className="fta-why-lead-row">
                                        <span className="fta-why-lead">{rc.driver}</span>
                                        <span className={`fta-impact-badge fta-impact-badge--${rc.impact.toLowerCase()}`}>{rc.impact}</span>
                                    </div>
                                    <p className="fta-why-detail">{rc.detail}</p>
                                </div>
                            </div>
                        ))}
                    </div>
                </section>
            )}

            {/* 4. Recommendations */}
            {agent.recommendations?.length > 0 && (
                <section className="fta-section">
                    <h3 className="fta-section-title">Recommendations</h3>
                    <ol className="fta-recs-list">
                        {agent.recommendations.map((rec) => (
                            <li
                                key={rec.priority}
                                className={`fta-rec-item${rec.type === 'marketplace' ? ' fta-rec-item--marketplace' : ''}`}
                            >
                                <div className="fta-rec-header">
                                    <span className="fta-rec-num">{rec.priority}</span>
                                    <span className="fta-rec-action">{rec.action}</span>
                                    {rec.type && (
                                        <span className={`fta-rec-tag fta-rec-tag--${rec.type}`}>
                                            <span className={`fta-rec-type-dot fta-rec-type-dot--${rec.type}`} />
                                            {rec.type}
                                        </span>
                                    )}
                                </div>
                                <p className="fta-rec-rationale">{rec.rationale}</p>
                                {!canEdit ? null : taskCreated.has(rec.priority) ? (
                                    <span className="fta-task-created-badge">
                                        <svg viewBox="0 0 20 20" fill="currentColor" width="12" height="12">
                                            <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z" clipRule="evenodd" />
                                        </svg>
                                        Task Created
                                    </span>
                                ) : (
                                    <button
                                        className="fta-create-task-btn"
                                        onClick={(e) => {
                                            e.stopPropagation();
                                            const pre = taskPrefill(rec, agent);
                                            setTaskFromRec(rec);
                                            setTaskTitle(pre.title);
                                            setTaskNotes(pre.notes);
                                            setTaskDescription(pre.description);
                                            setTaskType(pre.type);
                                            setTaskSuggestedPrice(pre.suggestedPrice);
                                            setTaskTargetContact(pre.targetContact);
                                        }}
                                    >
                                        <svg viewBox="0 0 20 20" fill="currentColor" width="12" height="12">
                                            <path d="M9 2a1 1 0 000 2h2a1 1 0 100-2H9z" />
                                            <path fillRule="evenodd" d="M4 5a2 2 0 012-2 3 3 0 003 3h2a3 3 0 003-3 2 2 0 012 2v11a2 2 0 01-2 2H6a2 2 0 01-2-2V5zm9.707 5.707a1 1 0 00-1.414-1.414L9 12.586l-1.293-1.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z" clipRule="evenodd" />
                                        </svg>
                                        Create Task
                                    </button>
                                )}
                            </li>
                        ))}
                    </ol>
                </section>
            )}

            {/* 4b. Marketplace Performance */}
            <MarketplacePerformance mp={agent.marketplace_performance} />

            {/* 5. Peer Breakdown */}
            {agent.peer_analysis?.length > 0 && (
                <section className="fta-section">
                    <h3 className="fta-section-title">Peer Breakdown</h3>
                    {agent.peer_insight && <p className="fta-peer-insight">{agent.peer_insight}</p>}
                    <div className="fta-peers-list">
                        {agent.peer_analysis.map((peer) => (
                            <div key={peer.stock_number} className={`fta-peer-row fta-peer-row--${peer.performance_vs_subject.toLowerCase().replace(' ', '-')}`}>
                                <div className="fta-peer-meta">
                                    <span className="fta-peer-stock">#{peer.stock_number}</span>
                                    <span className="fta-peer-price">${peer.asking_price?.toLocaleString('en-US')}</span>
                                    <span className="fta-peer-stat">{peer.hours?.toLocaleString('en-US')} hrs</span>
                                    <span className="fta-peer-stat">{peer.days_in_stock}d</span>
                                    <span className="fta-peer-stat">{peer.image_count} photos</span>
                                    <span className="fta-peer-stat">{peer.quote_count} quote{peer.quote_count === 1 ? '' : 's'}</span>
                                    <span className={`fta-peer-verdict fta-peer-verdict--${peer.performance_vs_subject.toLowerCase().replace(' ', '-')}`}>
                                        {peerVerdictArrow(peer.performance_vs_subject)} {peer.performance_vs_subject}
                                    </span>
                                </div>
                                <p className="fta-peer-differentiator">{peer.key_differentiator}</p>
                            </div>
                        ))}
                    </div>
                </section>
            )}

            {/* 6. Listing Comparison */}
            {agent.listing_comparison && (
                <section className="fta-section">
                    <h3 className="fta-section-title">Listing Comparison</h3>
                    <table className="fta-listing-table">
                        <thead>
                            <tr>
                                <th></th>
                                <th>This Unit</th>
                                <th>Peer Avg</th>
                            </tr>
                        </thead>
                        <tbody>
                            {agent.listing_comparison.subject_score != null && (
                                <tr>
                                    <td>Listing Quality Score</td>
                                    <td className={agent.listing_comparison.subject_score < (agent.listing_comparison.peer_avg_score || 0) ? 'fta-listing-cell--below-avg' : ''}>
                                        {agent.listing_comparison.subject_score}/6
                                    </td>
                                    <td>{agent.listing_comparison.peer_avg_score?.toFixed(1)}/6</td>
                                </tr>
                            )}
                            {agent.listing_comparison.subject_description_length != null && (
                                <tr>
                                    <td>Description Length</td>
                                    <td className={agent.listing_comparison.subject_description_length < (agent.listing_comparison.peer_avg_description_length || 0) ? 'fta-listing-cell--below-avg' : ''}>
                                        {agent.listing_comparison.subject_description_length} chars
                                    </td>
                                    <td>{agent.listing_comparison.peer_avg_description_length} chars</td>
                                </tr>
                            )}
                            {agent.listing_comparison.subject_options_count != null && (
                                <tr>
                                    <td>Options Listed</td>
                                    <td className={agent.listing_comparison.subject_options_count < (agent.listing_comparison.peer_avg_options_count || 0) ? 'fta-listing-cell--below-avg' : ''}>
                                        {agent.listing_comparison.subject_options_count}
                                    </td>
                                    <td>{agent.listing_comparison.peer_avg_options_count?.toFixed(1)}</td>
                                </tr>
                            )}
                            <tr>
                                <td>Listed Price vs System</td>
                                <td className={agent.listing_comparison.price_mismatch ? 'fta-listing-cell--mismatch' : ''} colSpan={2}>
                                    {mismatchText(agent.listing_comparison)}
                                </td>
                            </tr>
                        </tbody>
                    </table>
                    {agent.listing_comparison.listing_gap_summary && (
                        <p className="fta-listing-summary">{agent.listing_comparison.listing_gap_summary}</p>
                    )}
                </section>
            )}

            {/* 7. Market Context */}
            {agent.market_context && (
                <div className="fta-market-context">
                    <span className="fta-market-label">Market Context</span>
                    <p className="fta-market-text">{agent.market_context}</p>
                </div>
            )}

            {/* 8. Suggested MF Description */}
            {agent.suggested_description && (
                <section className="fta-section">
                    <div className="fta-desc-header">
                        <h3 className="fta-section-title">Suggested {SOURCE_LABELS.listing} Description</h3>
                        <button className="fta-copy-btn" onClick={handleCopy}>
                            {copied ? '✓ Copied' : 'Copy'}
                        </button>
                    </div>
                    <div className="fta-desc-box">
                        <p className="fta-desc-text">{agent.suggested_description}</p>
                    </div>
                </section>
            )}


            {/* Task Creation Modal from Recommendation */}
            {taskFromRec && (
                <div className="activity-modal-overlay" onClick={() => setTaskFromRec(null)}>
                    <div className="activity-modal" onClick={(e) => e.stopPropagation()}>
                        <div className="activity-modal-header">
                            <h3>Create Task from Recommendation</h3>
                            <span className="activity-modal-unit">Stock #{unit.StockNumber || unit.stock_number}</span>
                        </div>
                        <div className="activity-form-group">
                            <label>Type</label>
                            <select value={taskType} onChange={(e) => setTaskType(e.target.value)}>
                                <option value="price_change">Price Change</option>
                                <option value="photo_update">Photo Update</option>
                                <option value="description">Description Improvement</option>
                                <option value="other">Other</option>
                            </select>
                        </div>
                        <div className="activity-form-group">
                            <label>Title</label>
                            <textarea
                                value={taskTitle}
                                onChange={(e) => setTaskTitle(e.target.value)}
                                maxLength={800}
                                rows={3}
                            />
                        </div>
                        {taskType === 'price_change' && (
                            <div className="activity-form-row">
                                <div className="activity-form-group">
                                    <label>Current Price</label>
                                    <input type="text" value={formatCurrency(unit.Best_Sell_Price || unit.price)} disabled />
                                </div>
                                <div className="activity-form-group">
                                    <label>Suggested Price</label>
                                    <input
                                        type="number"
                                        value={taskSuggestedPrice}
                                        onChange={(e) => setTaskSuggestedPrice(e.target.value)}
                                        placeholder="e.g. 350000"
                                        min={0}
                                        step={1000}
                                    />
                                </div>
                            </div>
                        )}
                        {taskTargetContact && (
                            <div className="activity-form-group">
                                <label>Target Contact</label>
                                <input
                                    type="text"
                                    value={taskTargetContact}
                                    onChange={(e) => setTaskTargetContact(e.target.value)}
                                />
                            </div>
                        )}
                        <div className="activity-form-group">
                            <label>Notes</label>
                            <textarea value={taskNotes} onChange={(e) => setTaskNotes(e.target.value)} rows={4} />
                        </div>
                        {taskType === 'description' && taskDescription && (
                            <div className="activity-form-group">
                                <label>AI-Generated Description (will be included in task)</label>
                                <textarea value={taskDescription} onChange={(e) => setTaskDescription(e.target.value)} rows={5} style={{ fontSize: '0.78rem', lineHeight: 1.5 }} />
                            </div>
                        )}
                        <div className="activity-form-row">
                            <div className="activity-form-group">
                                <label>Assign to</label>
                                <UserSearch
                                    users={taskUsers}
                                    value={taskAssignee}
                                    onChange={setTaskAssignee}
                                    placeholder="Type to search..."
                                />
                            </div>
                            <div className="activity-form-group">
                                <label>Due date</label>
                                <input type="date" value={taskDueDate} onChange={(e) => setTaskDueDate(e.target.value)} />
                            </div>
                        </div>
                        <div className="activity-form-actions">
                            <button type="button" className="activity-btn-cancel" onClick={() => setTaskFromRec(null)}>Cancel</button>
                            <button type="button" className="activity-btn-create" onClick={handleCreateTask} disabled={taskCreating}>
                                {taskCreating ? 'Creating...' : 'Create Task'}
                            </button>
                        </div>
                    </div>
                </div>
            )}

        </div>
    );
}

FastTrackAgentTab.propTypes = {
    agent: PropTypes.shape({
        performance_verdict: PropTypes.oneOf(['Slow', 'Normal', 'Strong']),
        headline: PropTypes.string,
        urgency: PropTypes.oneOf(['High', 'Medium', 'Low']),
        urgency_reason: PropTypes.string,
        risk_tier: PropTypes.oneOf(['Red', 'Yellow', 'Green']),
        risk_level: PropTypes.oneOf(['High', 'Medium', 'Low']),
        risk_pct: PropTypes.number,
        root_causes: PropTypes.arrayOf(PropTypes.shape({
            driver: PropTypes.string,
            impact: PropTypes.oneOf(['High', 'Medium', 'Low']),
            detail: PropTypes.string,
        })),
        recommendations: PropTypes.arrayOf(PropTypes.shape({
            priority: PropTypes.number,
            action: PropTypes.string,
            rationale: PropTypes.string,
            type: PropTypes.oneOf(['pricing', 'retarget', 'listing', 'market', 'operational', 'marketplace']),
        })),
        peer_analysis: PropTypes.arrayOf(PropTypes.shape({
            stock_number: PropTypes.string,
            asking_price: PropTypes.number,
            hours: PropTypes.number,
            days_in_stock: PropTypes.number,
            image_count: PropTypes.number,
            quote_count: PropTypes.number,
            quote_velocity: PropTypes.number,
            performance_vs_subject: PropTypes.string,
            key_differentiator: PropTypes.string,
        })),
        peer_insight: PropTypes.string,
        listing_comparison: PropTypes.shape({
            subject_score: PropTypes.number,
            peer_avg_score: PropTypes.number,
            subject_description_length: PropTypes.number,
            peer_avg_description_length: PropTypes.number,
            subject_options_count: PropTypes.number,
            peer_avg_options_count: PropTypes.number,
            price_mismatch: PropTypes.bool,
            price_mismatch_delta: PropTypes.number,
            listing_gap_summary: PropTypes.string,
        }),
        market_context: PropTypes.string,
        marketplace_performance: PropTypes.object,
        cached: PropTypes.bool,
        generated_at: PropTypes.string,
    }),
    loading: PropTypes.bool,
    error: PropTypes.string,
    unit: PropTypes.object,
    onRegenerate: PropTypes.func.isRequired,
};

FastTrackAgentTab.defaultProps = {
    agent: null,
    loading: false,
    error: null,
    unit: null,
};

export default FastTrackAgentTab;
